import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LifeBuoy, Loader2, Plus, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import SavOpenDialog from "./SavOpenDialog";
import { fmtDate } from "./SavFields";

export function SavStatusBadge({ s }) {
  const tone = s.status === "done" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : s.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-sky-50 text-sky-800 border-sky-200";
  return <span className={`text-[11px] px-1.5 py-0.5 rounded border ${tone}`} data-testid={`sav-status-${s.id}`}>{s.status_label}</span>;
}

const rowText = (s) => (s.kind === "desinstallation"
  ? `Désinstallation${s.values?.date_rdv ? ` · prévue le ${fmtDate(s.values.date_rdv)}` : ""}${s.values?.description ? ` — ${s.values.description}` : ""}`
  : `${s.values?.type_sav || "—"} · ${s.values?.probleme_materiel || "matériel ?"} — ${s.values?.description || ""}`);

function DesinstBlock({ dossierId, existing }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const open = async () => {
    if (existing) return nav(`/sav/${existing.id}`);
    setBusy(true);
    try { const { data } = await api.post("/sav", { dossier_id: dossierId, kind: "desinstallation" }); toast.success(`Demande de désinstallation ${data.reference} ouverte`); nav(`/sav/${data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Ouverture impossible"); setBusy(false); }
  };
  return (
    <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-dashed border-norea-border px-4 py-3" data-testid="sav-desinst-block">
      <div className="text-sm"><span className="font-medium inline-flex items-center gap-1.5"><Undo2 className="w-4 h-4 text-norea-primary" />Désinstallation</span>
        <span className="text-norea-textMuted"> — {existing ? `demande ${existing.reference} · ${existing.status_label}` : "rétractation après la pose : protocole, désinstallation, annulation du financement."}</span></div>
      <button onClick={open} disabled={busy} className="btn-secondary text-sm" data-testid="sav-desinst-open-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />} {existing ? "Voir la demande de désinstallation" : "Ouvrir une demande de désinstallation"}
      </button>
    </div>
  );
}

export default function SavDossierTab({ dossier }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/sav", { params: { dossier_id: dossier.id } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [dossier.id]);
  return (
    <section className="card-flat p-5" data-testid="sav-dossier-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base font-medium inline-flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-norea-primary" />Demandes SAV du client</h3>
        <button onClick={() => setOpen(true)} className="btn-primary text-sm" data-testid="sav-open-btn"><Plus className="w-4 h-4" /> Ouvrir un SAV</button>
      </div>
      {items === null ? <div className="h-16 animate-pulse bg-norea-bg rounded" /> : items.length === 0 ? (
        <div className="py-6 text-center text-sm italic text-norea-textMuted" data-testid="sav-dossier-empty">Aucune demande SAV pour ce client.</div>
      ) : (
        <ul className="divide-y divide-norea-border" data-testid="sav-dossier-list">
          {items.map((s) => (
            <li key={s.id}><Link to={`/sav/${s.id}`} className="flex items-center gap-3 py-2.5 hover:bg-norea-surfaceHover px-2 -mx-2 rounded transition-colors" data-testid={`sav-row-${s.id}`}>
              <span className="font-mono text-xs text-norea-textMuted w-28">{s.reference}</span>
              <span className="text-sm flex-1 truncate">{rowText(s)}</span>
              <span className="text-xs text-norea-textMuted">{s.kind === "desinstallation" ? s.values?.equipe || "équipe à définir" : s.subcontractor_name || "sans sous-traitant"}</span>
              <span className="text-xs text-norea-textMuted w-20">{fmtDate(s.created_at)}</span>
              <SavStatusBadge s={s} />
            </Link></li>
          ))}
        </ul>
      )}
      {items !== null && <DesinstBlock dossierId={dossier.id} existing={items.find((s) => s.kind === "desinstallation")} />}
      {open && <SavOpenDialog dossierId={dossier.id} onClose={() => setOpen(false)} />}
    </section>
  );
}
